'use client'

import { forwardRef, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react'

import { PktIcon } from '../icon/Icon'
import { type IPktTag, PktTag } from '../tag/Tag'
import { useTabsContext } from './Tabs'

export type TSkin = IPktTag['skin']

export interface IPktTabItem {
  children: ReactNode
  active?: boolean
  href?: string
  onClick?: (event: MouseEvent) => void
  icon?: string
  controls?: string
  tag?: string
  tagSkin?: TSkin
  index?: number
}

export const PktTabItem = forwardRef<HTMLAnchorElement | HTMLButtonElement, IPktTabItem>(
  ({ children, active, href, onClick, icon, controls, tag, tagSkin, index = 0 }, ref) => {
    const { arrowNav, registerTabRef, handleKeyPress, selectTab } = useTabsContext()

    const handleClick = (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
      selectTab(index)
      onClick?.(event)
    }

    const commonProps = {
      'aria-selected': arrowNav ? !!active : undefined,
      'aria-controls': controls,
      role: arrowNav ? 'tab' : undefined,
      onKeyUp: (event: KeyboardEvent) => handleKeyPress(index, event),
      onClick: handleClick,
      tabIndex: active || !arrowNav ? undefined : -1,
      ref: (el: HTMLAnchorElement | HTMLButtonElement | null) => {
        registerTabRef(index, el)
        if (typeof ref === 'function') {
          ref(el)
        } else if (ref) {
          ref.current = el
        }
      },
    }

    const content = (
      <>
        {icon && <PktIcon name={icon} className="pkt-icon--small" />}
        {children}
        {tag && (
          <PktTag skin={tagSkin} size="small">
            {tag}
          </PktTag>
        )}
      </>
    )

    if (href) {
      return (
        <a {...commonProps} href={href} className={`pkt-tabs__link ${active ? 'active' : ''}`}>
          {content}
        </a>
      )
    }

    return (
      <button {...commonProps} type="button" className={`pkt-tabs__button pkt-link-button ${active ? 'active' : ''}`}>
        {content}
      </button>
    )
  },
)

PktTabItem.displayName = 'PktTabItem'
